import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import deviceStore from '@stores/deviceStore';
import { initializeApp } from 'firebase/app';
import { getMessaging, onMessage, getToken } from 'firebase/messaging';
import { useNavigate, Outlet } from 'react-router-dom';
import { useNotification } from './NotificationContext';
import OccConfirm from '@app/components/modal/OccConfirm';
import notificationStore from '@app/stores/notificationStore';

const firebaseConfig = {
  apiKey: 'AIzaSyBfvFWtHXmyqmz56ap17HCT5ZpviFdJhYU',
  authDomain: 'occ-kor.firebaseapp.com',
  projectId: 'occ-kor',
  storageBucket: 'occ-kor.appspot.com',
  messagingSenderId: '181446866160',
  appId: '1:181446866160:web:e802c424c900b9e5393e35',
  measurementId: 'G-7NX1417R29',
};

const isPWA =
  window.matchMedia('(display-mode: standalone)').matches ||
  window.navigator.standalone;
const serviceWorkerScope = '/firebase-cloud-messaging-push-scope/';

if ('serviceWorker' in navigator && isPWA) {
  navigator.serviceWorker
    .getRegistration(serviceWorkerScope)
    .then((registration) => {
      if (registration) {
        console.log('Service Worker registration found:', registration);

        // 서비스 워커가 활성 상태인지 확인
        const serviceWorker =
          registration.active ||
          registration.waiting ||
          registration.installing;

        if (serviceWorker) {
          // 서비스 워커가 활성 상태라면 메시지 보내기
          console.log('Service Worker is active:', serviceWorker);
          serviceWorker.postMessage({
            type: 'SET_PWA_STATE',
            isPWA: true,
          });
        } else {
          // 서비스 워커가 활성 상태가 아니라면 활성 상태가 될 때까지 기다림
          console.log('Service Worker is not active yet.');
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;

            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'activated') {
                console.log('Service Worker is now active:', newWorker);
                newWorker.postMessage({
                  type: 'SET_PWA_STATE',
                  isPWA: true,
                });
              }
            });
          });
        }
      } else {
        console.error(
          'No Service Worker registration found for scope:',
          serviceWorkerScope,
        );
      }
    })
    .catch((error) => {
      console.error('Error fetching Service Worker registration:', error);
    });
}

// Initialize Firebase
// Initialize Firebase Cloud Messaging and get a reference to the service
const firebaseApp = initializeApp(firebaseConfig);
const messaging = getMessaging(firebaseApp);

// FCMContext 생성
const FCMContext = createContext();

// FCMProvider 컴포넌트
export const FCMProvider = ({ children }) => {
  const { setFcmPushToken } = deviceStore();
  notificationStore();
  useNotification();
  const navigate = useNavigate();
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  // FCM 토큰과 서비스 워커 리스너는 최초 마운트 때만 등록하던 기존 수명주기를 유지합니다.
  const mountFcmRef = useRef({ navigate, setFcmPushToken });

  function isIOS() {
    // iOS인지 아닌지를 userAgent를 통해 간단히 판단할 수도 있음
    return /(iPad|iPhone|iPod)/g.test(navigator.userAgent);
  }

  useEffect(() => {
    const mountFcm = mountFcmRef.current;
    console.log('FCMProvider Mount');

    if (!isIOS()) {
      getFCMToken().then((token) => {
        mountFcm.setFcmPushToken(token);
      });
    } else {
      try {
        console.log('🍎 Notification : ', Notification);
        console.log('🍎 Notification.permission : ', Notification.permission);
        if (Notification.permission === 'default') {
          setConfirmModalOpen(true);
        } else if (Notification.permission === 'granted') {
          getToken(messaging, {
            vapidKey:
              'BOKAgXFrrUhj6vrY_M1zlvA-DCiFPFYoUkECtjcs4vOMBuFMJF_SbqTbKp--j-Vy2bpyVWgC3qo9sZzK4_EpemQ',
          })
            .then((currentToken) => {
              if (currentToken) {
                mountFcm.setFcmPushToken(currentToken);
              } else {
                console.log('토큰이 없습니다. 권한을 확인하세요.');
              }
            })
            .catch((err) => {
              console.log('토큰을 가져오는 중 오류가 발생했습니다.', err);
            });
        }
      } catch (e) {
        console.log(e);
      }
    }

    const unsubscribeForegroundMessage = onMessage(messaging, (payload) => {
      console.log(
        '🔥 [FOREGROUND] Message received.  => ',
        payload.notification,
      );
      // const notification = payload.notification;
      // incomeNoti(notification.title, notification.body);
    });

    // HTTP 비보안 환경에는 서비스 워커가 없을 수 있으므로, 사용 가능한 경우에만 등록한다.
    const serviceWorker = 'serviceWorker' in navigator && navigator.serviceWorker;
    const handleServiceWorkerMessage = (event) => {
      const message = event.data;
      if (message.type === 'navigate') {
        const route = message.route;
        console.log('Navigate to:', route);
        mountFcm.navigate(route);
      }
    };
    if (serviceWorker) {
      serviceWorker.addEventListener('message', handleServiceWorkerMessage);
    }

    return () => {
      if (typeof unsubscribeForegroundMessage === 'function') {
        unsubscribeForegroundMessage();
      }
      // 다시 마운트될 때 이전 리스너가 남지 않도록 등록한 리스너만 해제한다.
      if (serviceWorker) {
        serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
      }
    };
  }, []);

  async function getFCMToken() {
    const token = await Notification.requestPermission()
      .then((permission) => {
        console.log(`Notification permission ${permission}.`);
        if (permission === 'granted') {
          return getToken(messaging, {
            vapidKey:
              'BOKAgXFrrUhj6vrY_M1zlvA-DCiFPFYoUkECtjcs4vOMBuFMJF_SbqTbKp--j-Vy2bpyVWgC3qo9sZzK4_EpemQ',
          });
        } else {
          return null;
        }
      })
      .then((token) => {
        return token;
      })
      .catch(function (err) {
        console.debug('에러 : ', err);
        return null;
      });
    return token;
  }

  return (
    <FCMContext.Provider value={{}}>
      <OccConfirm
        open={confirmModalOpen}
        onCancel={() => setConfirmModalOpen(false)}
        onConfirm={() => {
          getFCMToken().then((token) => {
            setFcmPushToken(token);
          });
          setConfirmModalOpen(false);
        }}
      >
        <span
          style={{
            textAlign: 'center',
            fontSize: '16px',
            lineHeight: '26px',
          }}
        >
          원활한 사용을 위해 알림 허용이 필요합니다.
          <br />
          알림을 허용하시겠습니까?
        </span>
      </OccConfirm>
      <Outlet />
    </FCMContext.Provider>
  );
};

// useToast 훅
export const useFCM = () => {
  const context = useContext(FCMContext);
  if (!context) {
    throw new Error('useMessage must be used within a FCMProvider');
  }
  return context;
};
